import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  Box,
  Stack,
  Card,
  CardContent,
  CardActions,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Chip,
  Avatar,
  Divider,
  Button,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Badge,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import TagIcon from "@mui/icons-material/Tag";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import LanguageIcon from "@mui/icons-material/Language";
import BookOnlineIcon from "@mui/icons-material/BookOnline";

/**
 * Tipos de datos esperados (ejemplo)
 * threads: [{
 *   threadId: "t_123",
 *   channel: "Booking" | "Airbnb" | "Hostelworld" | "WhatsApp" | "Web",
 *   guestName: "John Doe",
 *   email: "john@mail.com",
 *   phone: "+549261...",
 *   lastMessageAt: ISODateString,
 *   unreadCount: 2,
 *   status: "open" | "pending" | "resolved" | "snoozed",
 *   reservation: {
 *      reservationId: "R-0001",
 *      startDate, endDate, nights, amount, balance, roomName
 *   },
 *   preview: "Hola, tengo una consulta...",
 *   tags: ["VIP","Recurrente"]
 * }]
 *
 * messagesByThread: Map<string, Array<Message>>
 * Message = {
 *   id: "m_01",
 *   threadId: "t_123",
 *   direction: "in" | "out",
 *   text: "Hola!",
 *   at: ISODateString,
 *   channel: same as thread,
 *   status: "sent" | "delivered" | "read" | "error" | "sending",
 *   attachments?: [{name, url, type}],
 * }
 */

const channelIcon = (ch) => {
  switch ((ch || "").toLowerCase()) {
    case "whatsapp":
      return <WhatsAppIcon fontSize="small" />;
    case "booking":
      return <BookOnlineIcon fontSize="small" />;
    case "airbnb":
      return <HomeWorkIcon fontSize="small" />;
    case "hostelworld":
      return <TravelExploreIcon fontSize="small" />;
    case "web":
      return <LanguageIcon fontSize="small" />;
    default:
      return <LanguageIcon fontSize="small" />;
  }
};

const formatDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};
const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const groupByDay = (msgs = []) => {
  const m = new Map();
  msgs.forEach((msg) => {
    const key = new Date(msg.at).toISOString().slice(0, 10);
    if (!m.has(key)) m.set(key, []);
    m.get(key).push(msg);
  });
  return Array.from(m.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([day, list]) => ({
      day,
      list: list.sort((a, b) => new Date(a.at) - new Date(b.at)),
    }));
};

const QuickReply = ({ text, onClick }) => (
  <Chip
    label={text}
    onClick={() => onClick(text)}
    sx={{ borderRadius: "12px" }}
    variant="outlined"
    size="small"
  />
);

function MessageBubble({ msg }) {
  const isOut = msg.direction === "out";
  const statusIcon = () => {
    if (msg.status === "read") return <DoneAllIcon fontSize="inherit" />;
    if (msg.status === "delivered") return <DoneAllIcon fontSize="inherit" />;
    if (msg.status === "sending") return <MoreVertIcon fontSize="inherit" />;
    if (msg.status === "error") return <ErrorOutlineIcon fontSize="inherit" />;
    return null;
  };

  return (
    <Stack
      direction="row"
      justifyContent={isOut ? "flex-end" : "flex-start"}
      sx={{ mb: 0.75 }}
    >
      <Box
        sx={{
          maxWidth: "78%",
          px: 1.2,
          py: 0.8,
          bgcolor: isOut ? "primary.main" : "background.paper",
          color: isOut ? "primary.contrastText" : "text.primary",
          borderRadius: isOut ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
          boxShadow: 1,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {msg.text || ""}
        </Typography>
        {msg.attachments?.length ? (
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {msg.attachments.map((a, i) => (
              <Button
                key={i}
                size="small"
                variant="text"
                onClick={() => window.open(a.url, "_blank")}
              >
                {a.name || a.type || "Archivo"}
              </Button>
            ))}
          </Stack>
        ) : null}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 0.5, opacity: 0.8 }}
        >
          <Typography variant="caption">{formatTime(msg.at)}</Typography>
          {isOut && (
            <Typography
              variant="caption"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}
            >
              {statusIcon()}
            </Typography>
          )}
        </Stack>
      </Box>
    </Stack>
  );
}

export default function MensajeDashboard({
  threads = [],
  messagesByThread = new Map(),
  onSendMessage, // (threadId, text, files) => Promise
  onLoadMore, // (threadId) => Promise (paginación)
  onResolve, // (threadId) => Promise
  onAssign, // (threadId, userId) => Promise
  onOpenReservation, // (reservationId) => void
  onTag, // (threadId, tag) => Promise
}) {
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("Todos");
  const [status, setStatus] = useState("open"); // open/pending/resolved/snoozed/Todos
  const [selectedThread, setSelectedThread] = useState(null);
  const [composer, setComposer] = useState("");
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);

  // Threads filtrados
  const channels = useMemo(
    () => [
      "Todos",
      ...Array.from(new Set(threads.map((t) => t.channel).filter(Boolean))),
    ],
    [threads]
  );
  const statuses = ["Todos", "open", "pending", "resolved", "snoozed"];

  const filteredThreads = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return threads
      .filter((t) => (channel === "Todos" ? true : t.channel === channel))
      .filter((t) => (status === "Todos" ? true : t.status === status))
      .filter(
        (t) =>
          !qq ||
          t.guestName?.toLowerCase().includes(qq) ||
          t.preview?.toLowerCase().includes(qq) ||
          t.reservation?.reservationId?.toLowerCase().includes(qq) ||
          t.email?.toLowerCase().includes(qq) ||
          (t.phone || "").toLowerCase().includes(qq)
      )
      .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
  }, [threads, q, channel, status]);

  // Autoselección primer thread
  useEffect(() => {
    if (!selectedThread && filteredThreads.length) {
      setSelectedThread(filteredThreads[0].threadId);
    }
  }, [filteredThreads, selectedThread]);

  const currentThread = useMemo(
    () => filteredThreads.find((t) => t.threadId === selectedThread) || null,
    [filteredThreads, selectedThread]
  );

  const currentMessages = useMemo(() => {
    const msgs = messagesByThread.get(selectedThread) || [];
    return groupByDay(msgs);
  }, [messagesByThread, selectedThread]);

  useEffect(() => {
    // Auto-scroll al final al cambiar de hilo o enviar
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [currentMessages, selectedThread]);

  // Enviar mensaje
  const handleSend = async () => {
    const text = composer.trim();
    if (!text || !currentThread) return;
    try {
      await onSendMessage?.(currentThread.threadId || currentThread, text, []);
      setComposer("");
    } catch (e) {
      console.error("Error al enviar:", e);
    }
  };

  // Quick replies sugeridos
  const quicks = [
    "¡Hola! ¿En qué puedo ayudarte?",
    "¿Cuántas personas y qué fechas te interesan?",
    "Te paso opciones y precios por aquí.",
    "¿Querés confirmar la reserva?",
  ];

  // UI
  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", md: "320px 1fr 360px" },
        height: { md: "calc(100vh - 160px)" },
      }}
    >
      {/* Columna izquierda: Filtros + lista */}
      <Stack spacing={1}>
        <Card sx={{ borderRadius: "16px" }}>
          <CardContent>
            <TextField
              fullWidth
              placeholder="Buscar por nombre, preview, ID reserva o contacto"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Canal</InputLabel>
                <Select
                  label="Canal"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  {channels.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {statuses.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            flex: 1,
            minHeight: 360,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <List
              sx={{
                py: 0,
                maxHeight: { md: "calc(100vh - 280px)" },
                overflow: "auto",
              }}
            >
              {filteredThreads.map((t) => (
                <ListItem
                  key={t.threadId}
                  button
                  selected={selectedThread === t.threadId}
                  onClick={() => setSelectedThread(t.threadId)}
                  alignItems="flex-start"
                  sx={{
                    py: 1.2,
                    "&.Mui-selected": { bgcolor: "action.selected" },
                  }}
                >
                  <Badge
                    color="primary"
                    badgeContent={t.unreadCount || 0}
                    invisible={!t.unreadCount}
                  >
                    <Avatar sx={{ mr: 1.2 }}>
                      {(t.guestName || "?").slice(0, 1).toUpperCase()}
                    </Avatar>
                  </Badge>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={0.8} alignItems="center">
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 700,
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.guestName || "Sin nombre"}
                        </Typography>
                        <Chip size="small" label={t.status || "open"} />
                        <Tooltip title={t.channel}>
                          {channelIcon(t.channel)}
                        </Tooltip>
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.4}>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t.preview || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDay(t.lastMessageAt)} ·{" "}
                          {t.reservation?.reservationId || "sin reserva"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.6}
                          sx={{ flexWrap: "wrap" }}
                        >
                          {(t.tags || []).map((tg, i) => (
                            <Chip
                              key={tg + i}
                              size="small"
                              label={tg}
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
              {!filteredThreads.length && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Sin conversaciones.
                  </Typography>
                </Box>
              )}
            </List>
          </CardContent>
        </Card>
      </Stack>

      {/* Centro: Chat */}
      <Card
        sx={{ borderRadius: "16px", display: "flex", flexDirection: "column" }}
      >
        <CardContent
          sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1.2 }}
        >
          {currentThread ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar>
                {(currentThread.guestName || "?").slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, lineHeight: 1 }}
                >
                  {currentThread.guestName || "Sin nombre"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {currentThread.email || "—"} · {currentThread.phone || "—"}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }} />
              <Chip
                size="small"
                label={currentThread.channel}
                icon={channelIcon(currentThread.channel)}
              />
              <Chip size="small" label={currentThread.status} />
              <IconButton>
                <MoreVertIcon />
              </IconButton>
            </Stack>
          ) : (
            <Typography variant="body2">Seleccioná una conversación</Typography>
          )}
        </CardContent>

        {/* Mensajes */}
        <Box ref={scrollRef} sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {currentMessages.length ? (
            currentMessages.map((group) => (
              <Box key={group.day} sx={{ mb: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "center", my: 1 }}>
                  <Chip size="small" label={formatDay(group.day)} />
                </Box>
                {group.list.map((m) => (
                  <MessageBubble key={m.id} msg={m} />
                ))}
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              Todavía no hay mensajes en este hilo.
            </Typography>
          )}
        </Box>

        {/* Composer */}
        <CardActions
          sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {quicks.map((qtxt, i) => (
                <QuickReply key={i} text={qtxt} onClick={setComposer} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title="Adjuntar (placeholder)">
                <span>
                  <IconButton
                    disabled={uploading}
                    onClick={() => setUploading(true)}
                  >
                    <AttachFileIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <TextField
                placeholder="Escribe un mensaje…"
                fullWidth
                value={composer}
                onChange={(e) => setComposer(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                multiline
                maxRows={4}
              />
              <Tooltip title="Enviar">
                <span>
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={!currentThread || !composer.trim()}
                  >
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardActions>
      </Card>

      {/* Derecha: Sidebar de reserva / acciones */}
      <Card
        sx={{
          borderRadius: "16px",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
        }}
      >
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Contexto de reserva
          </Typography>
          {currentThread?.reservation ? (
            <>
              <Stack spacing={0.4} sx={{ mb: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {currentThread.reservation.reservationId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentThread.reservation.roomName || "—"}
                </Typography>
                <Typography variant="body2">
                  {currentThread.reservation.startDate?.slice(0, 10)} →{" "}
                  {currentThread.reservation.endDate?.slice(0, 10)} ·{" "}
                  {currentThread.reservation.nights} noches
                </Typography>
                <Typography variant="body2">
                  Total: ${currentThread.reservation.amount || 0} · Saldo: $
                  {currentThread.reservation.balance || 0}
                </Typography>
              </Stack>
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", mb: 1 }}
              >
                <Chip size="small" icon={<TagIcon />} label="Cliente" />
                {(currentThread.tags || []).map((t, i) => (
                  <Chip key={t + i} size="small" label={t} variant="outlined" />
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<OpenInNewIcon />}
                  onClick={() =>
                    onOpenReservation?.(currentThread.reservation.reservationId)
                  }
                >
                  Abrir reserva
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => onResolve?.(currentThread.threadId)}
                >
                  Marcar resuelto
                </Button>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<PersonAddAltIcon />}
                  onClick={() => onAssign?.(currentThread.threadId, null)}
                >
                  Asignar
                </Button>
              </Stack>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No hay datos de reserva asociados.
            </Typography>
          )}
        </CardContent>

        <Divider />
        <CardContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Acciones rápidas
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Button
              size="small"
              onClick={() => onTag?.(currentThread?.threadId, "Pendiente pago")}
            >
              Añadir “Pendiente pago”
            </Button>
            <Button
              size="small"
              onClick={() => onTag?.(currentThread?.threadId, "Pedir reseña")}
            >
              Pedir reseña
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
